import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, COURSE_LIST, COURSES, WHATSAPP_CHANNEL_URL } from "@/lib/api";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import SiteEditor from "@/components/SiteEditor";
import {
  Loader2, LogOut, Download, Search, MessageCircle, ExternalLink, X, Check,
  Pencil, Trash2, AlertTriangle, Instagram, Save, BellRing, Users, UserX, Copy,
  Globe,
} from "lucide-react";

const PAYMENT_STATUSES = [
  { value: "pending_payment", label: "Pending Payment" },
  { value: "not_paid", label: "Not paid" },
  { value: "deposit_paid", label: "Deposit paid" },
  { value: "payment_plan", label: "Payment plan" },
  { value: "fully_paid", label: "Fully paid" },
];

const PAYMENT_METHODS = [
  { value: "tikkie", label: "Tikkie" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
];

const PAYMENT_OPTIONS = [
  { value: "pay_in_full", label: "Pay in Full" },
  { value: "deposit_remaining", label: "Deposit + Remaining Balance" },
  { value: "payment_plan", label: "Payment Plan" },
];

const statusColors = {
  pending_payment: "bg-yellow-200 text-yellow-900 border-yellow-300",
  not_paid: "bg-red-200 text-red-900 border-red-300",
  deposit_paid: "bg-blue-200 text-blue-900 border-blue-300",
  payment_plan: "bg-purple-200 text-purple-900 border-purple-300",
  fully_paid: "bg-green-200 text-green-900 border-green-300",
};

const optionColors = {
  pay_in_full: "bg-green-700/30 text-green-200 border-green-700/40",
  deposit_remaining: "bg-[#FF6A1A]/20 text-[#FF6A1A] border-[#FF6A1A]/40",
  payment_plan: "bg-blue-700/30 text-blue-200 border-blue-700/40",
};

function adminApi(method, path, body) {
  return api.request({
    url: path,
    method,
    data: body,
  });
}

function instagramUrl(handle) {
  if (!handle) return null;
  const clean = handle.replace(/^@/, "").trim();
  if (!clean) return null;
  return `https://instagram.com/${clean}`;
}

function labelFor(list, value) {
  return list.find((x) => x.value === value)?.label || "—";
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications"); // "applications" | "abandoned" | "website"
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOption, setFilterOption] = useState("all");
  const [filterWA, setFilterWA] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [abandoned, setAbandoned] = useState([]);
  const [abandonedLoading, setAbandonedLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCourse !== "all") params.append("course_id", filterCourse);
      if (filterStatus !== "all") params.append("payment_status_manual", filterStatus);
      if (filterOption !== "all") params.append("payment_option", filterOption);
      if (filterWA !== "all") params.append("whatsapp_added", filterWA);
      if (query.trim()) params.append("q", query.trim());
      const { data } = await adminApi("get", `/admin/orders?${params.toString()}`);
      const fresh = data.orders || [];
      setOrders(fresh);
      // Keep the open drawer in sync with the latest data
      setSelected((curr) => {
        if (!curr) return curr;
        const match = fresh.find((o) => o.order_id === curr.order_id);
        return match || curr;
      });
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    // Verify the session cookie is still valid; if not, route back to login.
    api.get("/admin/me")
      .then(() => { if (!cancelled) load(); })
      .catch(() => { if (!cancelled) navigate("/admin"); });
    return () => { cancelled = true; };
    // load/navigate are stable refs in this component; we intentionally re-run only when filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCourse, filterStatus, filterOption, filterWA]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch { /* ignore */ }
    navigate("/admin");
  };

  const loadAbandoned = async () => {
    setAbandonedLoading(true);
    try {
      const { data } = await adminApi("get", "/admin/orders/abandoned");
      setAbandoned(data.orders || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin");
      }
    } finally {
      setAbandonedLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "abandoned") loadAbandoned();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/orders.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "applications.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Export failed."); }
  };

  const sendWhatsApp = (order) => {
    const phone = (order.phone || "").replace(/[^0-9]/g, "");
    if (!phone) { alert("No phone number on file."); return; }
    let intl = phone;
    if (intl.startsWith("0")) intl = "31" + intl.slice(1);
    const message = `Hi ${order.first_name}, thank you for booking your Pattern Texture course. Here is the link to join our private WhatsApp channel for course updates: ${WHATSAPP_CHANNEL_URL}`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const stats = useMemo(() => {
    const total = orders.length;
    const paid = orders.filter((o) => o.payment_status_manual === "fully_paid").length;
    const deposit = orders.filter((o) => o.payment_status_manual === "deposit_paid").length;
    const pending = orders.filter((o) => o.payment_status_manual === "pending_payment").length;
    const wa = orders.filter((o) => o.whatsapp_added).length;
    return { total, paid, deposit, pending, wa };
  }, [orders]);

  const remainingByCourse = useMemo(() => {
    const result = {};
    for (const c of COURSE_LIST) {
      const taken = orders.filter((o) => o.course_id === c.id && (o.payment_status_manual === "fully_paid" || o.payment_status_manual === "deposit_paid")).length;
      result[c.id] = { total: 6, taken, remaining: Math.max(0, 6 - taken) };
    }
    return result;
  }, [orders]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 p-4 sm:p-6">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase font-mono text-[#FF6A1A]/80">Pattern Texture Room</div>
            <h1 className="text-3xl sm:text-4xl font-semibold mt-1">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsv} className="flex items-center gap-2 bg-[#FF6A1A] text-black font-semibold px-4 py-2 rounded-full text-sm hover:opacity-90" data-testid="export-csv">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={logout} className="flex items-center gap-2 bg-neutral-800 text-neutral-200 font-medium px-4 py-2 rounded-full text-sm hover:bg-neutral-700" data-testid="logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <AnalyticsPanel />

        {/* Tab switcher */}
        <div className="flex gap-2 mb-4" data-testid="admin-tabs">
          <button
            onClick={() => setTab("applications")}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${
              tab === "applications" ? "bg-[#FF6A1A] text-black" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
            data-testid="tab-applications"
          >
            <Users size={12} /> Aanmeldingen
          </button>
          <button
            onClick={() => setTab("abandoned")}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${
              tab === "abandoned" ? "bg-[#FF6A1A] text-black" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
            data-testid="tab-abandoned"
          >
            <UserX size={12} /> Niet-afgemaakt
            {abandoned.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
                {abandoned.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("website")}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${
              tab === "website" ? "bg-[#FF6A1A] text-black" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
            data-testid="tab-website"
          >
            <Globe size={12} /> Website aanpassen
          </button>
        </div>

        {tab === "website" ? (
          <SiteEditor />
        ) : tab === "abandoned" ? (
          <AbandonedList
            rows={abandoned}
            loading={abandonedLoading}
            onChanged={loadAbandoned}
          />
        ) : (
          <>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Fully paid" value={stats.paid} color="text-green-400" />
          <StatCard label="Deposit" value={stats.deposit} color="text-blue-300" />
          <StatCard label="Pending payment" value={stats.pending} color="text-yellow-400" />
          <StatCard label="In WhatsApp" value={stats.wa} color="text-[#FF6A1A]" />
        </div>

        {/* Per-course spots */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {COURSE_LIST.map((c) => {
            const r = remainingByCourse[c.id];
            return (
              <div key={c.id} className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
                <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{c.name}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-[#FF6A1A]">{r.remaining}</span>
                  <span className="text-sm text-neutral-400">/ {r.total} spots remaining</span>
                </div>
                <div className="text-[10px] text-neutral-500 mt-0.5">{r.taken} reserved (deposit or fully paid)</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
          <div className="relative sm:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, email…" className="w-full bg-neutral-900 border border-neutral-800 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid="search-input" />
          </div>
          <Select value={filterCourse} onChange={setFilterCourse} testId="filter-course">
            <option value="all">All courses</option>
            {COURSE_LIST.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={filterStatus} onChange={setFilterStatus} testId="filter-status">
            <option value="all">All payment statuses</option>
            {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Select value={filterOption} onChange={setFilterOption} testId="filter-option">
            <option value="all">All payment options</option>
            {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <Select value={filterWA} onChange={setFilterWA} testId="filter-whatsapp">
            <option value="all">All WhatsApp states</option>
            <option value="yes">WhatsApp: Yes</option>
            <option value="no">WhatsApp: No</option>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-neutral-800/60">
                <tr className="text-left text-xs uppercase tracking-wide text-neutral-400">
                  <Th>Full name</Th>
                  <Th>Course</Th>
                  <Th>Payment option</Th>
                  <Th>Payment method</Th>
                  <Th>Status</Th>
                  <Th>Phone</Th>
                  <Th>Email</Th>
                  <Th>WhatsApp</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="text-center py-10"><Loader2 className="animate-spin inline" /></td></tr>}
                {!loading && orders.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-neutral-500">No applications found.</td></tr>}
                {!loading && orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-neutral-800 hover:bg-neutral-800/40">
                    <Td>
                      <button
                        onClick={() => setSelected(o)}
                        className="font-semibold text-[#FF6A1A] hover:underline text-left"
                        data-testid={`name-${o.order_id}`}
                      >
                        {o.first_name} {o.last_name}
                      </button>
                      <div className="text-[10px] text-neutral-500 mt-0.5">{(o.created_at || "").slice(0,10)}</div>
                    </Td>
                    <Td>
                      <div className="text-xs font-semibold">{o.course_display_name || o.course_name}</div>
                      <div className="text-[11px] text-neutral-400">{o.course_dates_display}</div>
                    </Td>
                    <Td><OptionBadge option={o.payment_option} /></Td>
                    <Td className="text-xs">{o.payment_method_label || "—"}</Td>
                    <Td><PaymentBadge status={o.payment_status_manual || "pending_payment"} /></Td>
                    <Td className="text-xs">{o.phone || "—"}</Td>
                    <Td className="text-xs"><span className="break-all">{o.email || "—"}</span></Td>
                    <Td><WaToggle order={o} onChanged={load} /></Td>
                    <Td>
                      <div className="flex flex-col gap-1.5">
                        <button onClick={() => setSelected(o)} className="text-xs bg-neutral-800 hover:bg-neutral-700 px-3 py-1.5 rounded-full" data-testid={`open-${o.order_id}`}>
                          View Details
                        </button>
                        {o.payment_status_manual === "fully_paid" && (
                          <button onClick={() => sendWhatsApp(o)} className="text-xs bg-[#25D366] text-black font-semibold px-3 py-1.5 rounded-full flex items-center justify-center gap-1.5 hover:opacity-90" data-testid={`whatsapp-${o.order_id}`}>
                            <MessageCircle size={12} /> Send Invite
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}
      </div>

      {selected && <OrderDrawer order={selected} onClose={() => setSelected(null)} onChanged={load} onDeleted={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function StatCard({ label, value, color = "text-neutral-100" }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>; }

function Select({ value, onChange, children, testId }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid={testId}>{children}</select>;
}

function PaymentBadge({ status }) {
  const s = PAYMENT_STATUSES.find((p) => p.value === status) || PAYMENT_STATUSES[0];
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statusColors[status] || statusColors.pending_payment}`}>{s.label}</span>;
}

function OptionBadge({ option }) {
  if (!option) return <span className="text-neutral-500 text-xs">—</span>;
  const label = labelFor(PAYMENT_OPTIONS, option);
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold border ${optionColors[option] || "bg-neutral-800 text-neutral-300 border-neutral-700"}`}>{label}</span>;
}

function WaToggle({ order, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await adminApi("patch", `/admin/orders/${order.order_id}`, { whatsapp_added: !order.whatsapp_added });
      onChanged();
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${order.whatsapp_added ? "bg-[#25D366]/20 border-[#25D366]/40 text-[#25D366]" : "bg-neutral-800 border-neutral-700 text-neutral-300"}`} data-testid={`wa-toggle-${order.order_id}`}>
      {order.whatsapp_added ? <Check size={11} /> : <X size={11} />}
      {order.whatsapp_added ? "Yes" : "No"}
    </button>
  );
}

function OrderDrawer({ order, onClose, onChanged, onDeleted }) {
  const [edit, setEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    first_name: order.first_name || "",
    last_name: order.last_name || "",
    phone: order.phone || "",
    email: order.email || "",
    instagram: order.instagram || "",
    course_id: order.course_id || "",
    payment_status_manual: order.payment_status_manual || "pending_payment",
    payment_method: order.payment_method || "",
    payment_option: order.payment_option || "",
    notes: order.notes || "",
    goal: order.goal || "",
    want_to_learn: order.want_to_learn || "",
    dietary_preferences: order.dietary_preferences || "",
    food_dislikes: order.food_dislikes || "",
    allergies: order.allergies || "",
    can_braid: !!order.can_braid,
    can_weave: !!order.can_weave,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi("patch", `/admin/orders/${order.order_id}`, form);
      onChanged();
      setEdit(false);
    } catch (err) {
      alert(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await adminApi("delete", `/admin/orders/${order.order_id}`);
      onDeleted();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  const ig = instagramUrl(form.instagram);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-neutral-950 border-l border-neutral-800 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-drawer">
        <div className="sticky top-0 bg-neutral-950/95 backdrop-blur border-b border-neutral-800 p-5 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Application</div>
            <div className="font-semibold mt-0.5 truncate">{order.first_name} {order.last_name}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{order.order_id}</div>
          </div>
          <div className="flex items-center gap-2">
            {!edit && (
              <button onClick={() => setEdit(true)} className="text-xs bg-[#FF6A1A] text-black font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="edit-toggle">
                <Pencil size={12} /> Edit
              </button>
            )}
            {!edit && (
              <button onClick={() => setConfirmDelete(true)} className="text-xs bg-red-600 text-white font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="delete-button">
                <Trash2 size={12} /> Delete
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Personal info">
            <div className="grid grid-cols-2 gap-3">
              <EditableField label="First name" value={form.first_name} onChange={(v) => update("first_name", v)} edit={edit} />
              <EditableField label="Last name" value={form.last_name} onChange={(v) => update("last_name", v)} edit={edit} />
              <EditableField label="Phone" value={form.phone} onChange={(v) => update("phone", v)} edit={edit} />
              <EditableField label="Email" value={form.email} onChange={(v) => update("email", v)} edit={edit} type="email" />
              <div className="col-span-2">
                <EditableField
                  label="Instagram"
                  value={form.instagram}
                  onChange={(v) => update("instagram", v)}
                  edit={edit}
                  rightSlot={!edit && ig ? (
                    <a href={ig} target="_blank" rel="noopener noreferrer" className="text-[#FF6A1A] text-xs flex items-center gap-1 hover:underline">
                      <Instagram size={12} /> open <ExternalLink size={10} />
                    </a>
                  ) : null}
                />
              </div>
            </div>
          </Section>

          <Section title="Course & payment">
            <label className="block mb-3">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">Selected course</span>
              {edit ? (
                <select value={form.course_id} onChange={(e) => update("course_id", e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid="edit-course">
                  {Object.values(COURSES).map((c) => <option key={c.id} value={c.id}>{c.name} — €{c.price}</option>)}
                </select>
              ) : (
                <div className="text-sm">
                  {order.course_display_name || order.course_name}
                  <div className="text-[11px] text-neutral-400">{order.course_dates_display} · €{Number(order.amount).toLocaleString("en-US")}</div>
                </div>
              )}
            </label>

            <label className="block mb-3">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">Payment option (chosen by student)</span>
              {edit ? (
                <select value={form.payment_option} onChange={(e) => update("payment_option", e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid="drawer-payment-option">
                  <option value="">—</option>
                  {PAYMENT_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              ) : (
                <div data-testid="drawer-payment-option-view"><OptionBadge option={order.payment_option} /></div>
              )}
            </label>

            <label className="block mb-3">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">Payment method (chosen by student)</span>
              {edit ? (
                <select value={form.payment_method} onChange={(e) => update("payment_method", e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid="drawer-payment-method">
                  <option value="">—</option>
                  {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              ) : (
                <div className="text-sm" data-testid="drawer-payment-method-view">{labelFor(PAYMENT_METHODS, order.payment_method)}</div>
              )}
            </label>

            <label className="block">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">Payment status</span>
              <select value={form.payment_status_manual} onChange={(e) => update("payment_status_manual", e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" data-testid="drawer-status">
                {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <p className="text-[10px] text-neutral-500 mt-1">A spot is reserved when status is &quot;Deposit paid&quot; or &quot;Fully paid&quot;.</p>
            </label>
          </Section>

          <Section title="Skills">
            {edit ? (
              <div className="grid grid-cols-2 gap-3">
                <YesNoEdit label="Can braid?" value={form.can_braid} onChange={(v) => update("can_braid", v)} />
                <YesNoEdit label="Can install?" value={form.can_weave} onChange={(v) => update("can_weave", v)} />
              </div>
            ) : (
              <div className="space-y-2">
                <Detail label="Can braid?" value={order.can_braid ? "Yes" : "No"} />
                <Detail label="Can do hair installations?" value={order.can_weave ? "Yes" : "No"} />
              </div>
            )}
          </Section>

          <Section title="Goal & background">
            <EditableField label="Goal with the course" value={form.goal} onChange={(v) => update("goal", v)} edit={edit} multiline rows={3} />
            <EditableField label="Wants to learn" value={form.want_to_learn} onChange={(v) => update("want_to_learn", v)} edit={edit} multiline rows={2} />
            <Detail label="Heard about us" value={order.heard_about || "—"} />
          </Section>

          <Section title="Food & allergies">
            <EditableField label="Dietary preferences" value={form.dietary_preferences} onChange={(v) => update("dietary_preferences", v)} edit={edit} />
            <EditableField label="Foods they don't eat" value={form.food_dislikes} onChange={(v) => update("food_dislikes", v)} edit={edit} />
            <EditableField label="Allergies" value={form.allergies} onChange={(v) => update("allergies", v)} edit={edit} highlight={form.allergies ? "text-red-300" : ""} />
          </Section>

          <Section title="Private notes">
            <textarea rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF6A1A]" placeholder="Sent Tikkie link, waiting for transfer, called student..." data-testid="drawer-notes" />
          </Section>

          <PaymentPlanSection order={order} onChanged={onChanged} />

          <ReminderSection order={order} onChanged={onChanged} />

          <Section title="Meta">
            <Detail label="Order ID" value={order.order_id} />
            <Detail label="Date applied" value={order.created_at} />
          </Section>

          <button onClick={save} disabled={saving} className="w-full bg-[#FF6A1A] text-black font-semibold py-3 rounded-full hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="drawer-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            <span>{saving ? "Saving…" : "Save changes"}</span>
          </button>

          <a href={`mailto:${order.email}`} className="block text-center text-sm text-neutral-400 hover:text-neutral-200 underline">
            <ExternalLink size={12} className="inline mr-1" /> Reply via email
          </a>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmDelete(false)}>
          <div className="bg-neutral-900 border-2 border-red-700 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-700/20 border border-red-700 flex items-center justify-center">
                <AlertTriangle size={18} className="text-red-400" />
              </div>
              <div>
                <div className="font-semibold">Delete this application?</div>
                <div className="text-xs text-neutral-400">This cannot be undone.</div>
              </div>
            </div>
            <p className="text-sm text-neutral-300 mt-2">
              <strong>{order.first_name} {order.last_name}</strong> — {order.course_display_name || order.course_name}
            </p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDelete(false)} className="flex-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-medium py-2.5 rounded-full text-sm">Cancel</button>
              <button onClick={doDelete} className="flex-1 bg-red-600 hover:bg-red-500 text-white font-semibold py-2.5 rounded-full text-sm flex items-center justify-center gap-2" data-testid="confirm-delete-yes">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{label}</div>
      <div className="text-sm mt-0.5 break-words whitespace-pre-wrap">{value || "—"}</div>
    </div>
  );
}

function EditableField({ label, value, onChange, edit, type = "text", multiline = false, rows = 1, rightSlot = null, highlight = "" }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{label}</span>
        {rightSlot}
      </div>
      {edit ? (
        multiline ? (
          <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6A1A]" />
        ) : (
          <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6A1A]" />
        )
      ) : (
        <div className={`text-sm break-words whitespace-pre-wrap ${highlight}`}>{value || "—"}</div>
      )}
    </div>
  );
}

function YesNoEdit({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onChange(true)} className={`py-2 rounded-lg text-xs font-semibold ${value ? "bg-[#FF6A1A] text-black" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"}`}>Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`py-2 rounded-lg text-xs font-semibold ${!value ? "bg-[#FF6A1A] text-black" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"}`}>No</button>
      </div>
    </div>
  );
}

function PaymentPlanSection({ order, onChanged }) {
  const plan = order.payment_plan;
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const defaultDates = [
    new Date(Date.now() + 86400000 * 7).toISOString().slice(0, 10),
    new Date(Date.now() + 86400000 * 37).toISOString().slice(0, 10),
    new Date(Date.now() + 86400000 * 67).toISOString().slice(0, 10),
  ];
  const [dates, setDates] = useState(plan?.installments?.map((i) => i.due_date) || defaultDates);

  const createPlan = async () => {
    setCreating(true);
    try {
      await adminApi("post", `/admin/orders/${order.order_id}/payment-plan`, { due_dates: dates });
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const deletePlan = async () => {
    if (!window.confirm("Cancel this payment plan? Installment data will be removed.")) return;
    try {
      await adminApi("delete", `/admin/orders/${order.order_id}/payment-plan`);
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (n, status) => {
    setBusy(n);
    try {
      await adminApi("patch", `/admin/orders/${order.order_id}/installments/${n}`, { status });
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const statusStyles = {
    pending: "bg-neutral-700 text-neutral-200",
    paid: "bg-green-700/40 text-green-300",
  };
  const statusLabels = {
    pending: "Pending",
    paid: "Paid",
  };

  if (!plan?.enabled) {
    return (
      <Section title="Payment plan">
        <p className="text-xs text-neutral-400 mb-3">
          Set up a 3-installment payment plan for this student. The course amount (€{Number(order.amount).toLocaleString("en-US")}) will be split equally. You track each installment manually.
        </p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[0, 1, 2].map((idx) => (
            <label key={idx} className="block">
              <span className="block text-[9px] tracking-[0.2em] uppercase text-neutral-500 mb-1">Installment {idx + 1}</span>
              <input
                type="date"
                min={today}
                value={dates[idx]}
                onChange={(e) => setDates((d) => d.map((v, i) => i === idx ? e.target.value : v))}
                className="w-full bg-neutral-900 border border-neutral-800 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#FF6A1A]"
                data-testid={`plan-date-${idx + 1}`}
              />
            </label>
          ))}
        </div>
        <button
          onClick={createPlan}
          disabled={creating}
          className="w-full bg-[#FF6A1A] text-black font-semibold py-2.5 rounded-full text-sm hover:opacity-90 disabled:opacity-50"
          data-testid="create-plan"
        >
          {creating ? "Creating…" : "Create Payment Plan"}
        </button>
      </Section>
    );
  }

  return (
    <Section title="Payment plan">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-neutral-400">3 installments · €{plan.course_amount} total</div>
        <button onClick={deletePlan} className="text-[11px] text-red-400 hover:text-red-300 underline">Cancel plan</button>
      </div>

      <div className="space-y-2">
        {plan.installments.map((inst) => {
          const isPaid = inst.status === "paid";
          return (
            <div key={inst.number} className="bg-neutral-900 border border-neutral-800 rounded-xl p-3" data-testid={`installment-${inst.number}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold">Installment {inst.number} / 3</div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">
                    €{Number(inst.amount).toFixed(2)} · {inst.due_date}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${statusStyles[inst.status]}`}>
                  {statusLabels[inst.status]}
                </span>
              </div>
              {inst.paid_at && (
                <div className="mt-1 text-[10px] text-green-400">Paid on {inst.paid_at.slice(0, 10)}</div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {isPaid ? (
                  <button
                    onClick={() => setStatus(inst.number, "pending")}
                    disabled={busy === inst.number}
                    className="text-[11px] bg-neutral-700 text-neutral-100 font-medium px-3 py-1.5 rounded-full disabled:opacity-40"
                    data-testid={`mark-pending-${inst.number}`}
                  >
                    Mark as pending
                  </button>
                ) : (
                  <button
                    onClick={() => setStatus(inst.number, "paid")}
                    disabled={busy === inst.number}
                    className="text-[11px] bg-[#FF6A1A] text-black font-semibold px-3 py-1.5 rounded-full disabled:opacity-40"
                    data-testid={`mark-paid-${inst.number}`}
                  >
                    {busy === inst.number ? "…" : "Mark as paid"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[10px] text-neutral-500 leading-relaxed">
        Track installments manually. When all 3 are marked paid, the order is automatically marked as &quot;Fully paid&quot; and a spot is deducted.
      </div>
    </Section>
  );
}


function ReminderSection({ order, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isPending = order.payment_status_manual === "pending_payment";
  const lastSent = order.last_reminder_at ? order.last_reminder_at.slice(0, 16).replace("T", " ") : null;

  const send = async () => {
    if (!window.confirm(`Send a payment reminder email to ${order.email}?`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi("post", `/admin/orders/${order.order_id}/send-reminder`);
      if (data.sent) {
        onChanged();
      } else {
        alert("Reminder could not be sent (check email config).");
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Payment reminder">
      <p className="text-xs text-neutral-400 leading-relaxed mb-3">
        Auto-reminders are sent ~24h after application for orders still pending payment. You can also send one manually here.
      </p>
      <div className="flex items-center justify-between gap-3 bg-neutral-900 border border-neutral-800 rounded-xl p-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Reminder status</div>
          <div className="text-sm font-medium mt-0.5">
            {order.reminder_sent ? <span className="text-green-400">Sent</span> : <span className="text-neutral-400">Not sent</span>}
          </div>
          {lastSent && <div className="text-[10px] text-neutral-500 mt-0.5">Last: {lastSent}</div>}
        </div>
        <button
          onClick={send}
          disabled={busy || !isPending}
          title={!isPending ? "Reminders only apply to 'Pending Payment' orders" : ""}
          className="text-xs bg-[#FF6A1A] text-black font-semibold px-4 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="send-reminder-button"
        >
          <BellRing size={12} />
          {busy ? "Sending…" : order.reminder_sent ? "Send again" : "Send reminder"}
        </button>
      </div>
    </Section>
  );
}


/* ============= Abandoned Registrations ============= */

function buildFollowupText(order) {
  const name = order.first_name || "";
  return `Hey ${name}, ik zag dat je interesse had in de 3-daagse Weave Met Leave-Out cursus van Pattern Texture Academy, maar je aanmelding is nog niet helemaal afgerond. Twijfel je nog ergens over? Je mag mij gerust appen, dan kijk ik eerlijk met je mee. Liefs, Quimarich`;
}

function toWaLink(phone, text) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  let intl = digits;
  if (intl.startsWith("0")) intl = "31" + intl.slice(1);
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
}

function AbandonedList({ rows, loading, onChanged }) {
  const [busy, setBusy] = useState(null);

  const sendReminder = async (orderId) => {
    setBusy(`rem-${orderId}`);
    try {
      const { data } = await adminApi("post", `/admin/orders/${orderId}/send-reminder`);
      if (!data.sent) alert("Reminder kon niet verstuurd worden.");
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Mislukt"); }
    finally { setBusy(null); }
  };

  const markPaid = async (orderId) => {
    setBusy(`paid-${orderId}`);
    try {
      await adminApi("patch", `/admin/orders/${orderId}`, { payment_status_manual: "fully_paid" });
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Mislukt"); }
    finally { setBusy(null); }
  };

  const cancel = async (orderId, name) => {
    if (!window.confirm(`Aanmelding van ${name} annuleren? Dit verwijdert de aanmelding permanent.`)) return;
    try {
      await adminApi("delete", `/admin/orders/${orderId}`);
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Mislukt"); }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Bericht gekopieerd naar klembord.");
    } catch { alert("Kopiëren mislukt."); }
  };

  if (loading) {
    return (
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 text-center text-neutral-400">
        <Loader2 className="animate-spin inline" /> Laden…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 text-center" data-testid="abandoned-empty">
        <div className="text-2xl mb-2">🎉</div>
        <div className="font-semibold">Geen niet-afgemaakte aanmeldingen</div>
        <div className="text-xs text-neutral-500 mt-1">Iedereen heeft betaald — mooi werk!</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="abandoned-list">
      <div className="text-xs text-neutral-400">
        <strong className="text-[#FF6A1A]">{rows.length}</strong> aanmelding(en) hebben nog niet betaald (ouder dan 15 min).
      </div>
      {rows.map((r) => {
        const followupText = buildFollowupText(r);
        const waUrl = toWaLink(r.phone, followupText);
        const applied = (r.created_at || "").slice(0, 16).replace("T", " ");
        return (
          <div key={r.order_id} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4" data-testid={`abandoned-${r.order_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-base text-[#FF6A1A]">
                  {r.first_name} {r.last_name}
                </div>
                <div className="text-[11px] text-neutral-400 font-mono mt-0.5">
                  {r.order_id} · {applied}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                  <div><span className="text-neutral-500">E-mail:</span> <span className="break-all">{r.email}</span></div>
                  <div><span className="text-neutral-500">Telefoon:</span> {r.phone || "—"}</div>
                  <div><span className="text-neutral-500">Instagram:</span> {r.instagram || "—"}</div>
                  <div><span className="text-neutral-500">Cursus:</span> {r.course_display_name}</div>
                  <div><span className="text-neutral-500">Betaaloptie:</span> {r.payment_option_label}</div>
                  <div><span className="text-neutral-500">Bedrag:</span> €{Number(r.amount).toLocaleString("nl-NL")}</div>
                  <div><span className="text-neutral-500">Laatste stap:</span> <span className="text-yellow-400 font-semibold">Pending Payment</span></div>
                  <div>
                    <span className="text-neutral-500">Herinnering:</span>{" "}
                    {r.reminder_sent
                      ? <span className="text-green-400 font-semibold">Ja</span>
                      : <span className="text-neutral-400">Nee</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Follow-up preview */}
            <div className="mt-3 bg-black/40 border border-neutral-800 rounded-xl p-3 text-[11px] text-neutral-300 leading-relaxed">
              <div className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">↳ WhatsApp bericht</div>
              {followupText}
            </div>

            {/* Actions */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => sendReminder(r.order_id)}
                disabled={busy === `rem-${r.order_id}`}
                className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-medium px-3 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-40"
                data-testid={`ab-remind-${r.order_id}`}
              >
                <BellRing size={11} /> Stuur herinneringsmail
              </button>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-[#25D366] text-black font-semibold px-3 py-2 rounded-full flex items-center gap-1.5 hover:opacity-90"
                data-testid={`ab-wa-${r.order_id}`}
              >
                <MessageCircle size={11} /> Open WhatsApp
              </a>
              <button
                onClick={() => copyText(followupText)}
                className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-medium px-3 py-2 rounded-full flex items-center gap-1.5"
                data-testid={`ab-copy-${r.order_id}`}
              >
                <Copy size={11} /> Kopieer tekst
              </button>
              <button
                onClick={() => markPaid(r.order_id)}
                disabled={busy === `paid-${r.order_id}`}
                className="text-xs bg-green-600 hover:bg-green-500 text-white font-semibold px-3 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-40"
                data-testid={`ab-paid-${r.order_id}`}
              >
                <Check size={11} /> Markeer als betaald
              </button>
              <button
                onClick={() => cancel(r.order_id, `${r.first_name} ${r.last_name}`)}
                className="text-xs bg-red-600 hover:bg-red-500 text-white font-semibold px-3 py-2 rounded-full flex items-center gap-1.5"
                data-testid={`ab-cancel-${r.order_id}`}
              >
                <Trash2 size={11} /> Annuleer
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
